import {useTranslation} from 'react-i18next'
import type {TFunction} from 'i18next'
import {cn} from '@/lib/utils'

/** Solo la lectura del trayecto; las transiciones viven en el checklist de Gestión. */
const ORDEN = ['reportado', 'reportado_arl', 'en_investigacion', 'cerrado'] as const
type EstadoPaso = (typeof ORDEN)[number]
type PasoEstado = 'hecho' | 'actual' | 'saltado' | 'pendiente'

export const ESTADO_TONE: Record<string, 'neutral' | 'amber' | 'green'> = {
  reportado: 'neutral',
  reportado_arl: 'amber',
  en_investigacion: 'amber',
  cerrado: 'green',
}

/** Chip de ≤ 3 palabras; la frase completa viaja al `porque` de `CeldaEstado`. */
export function estadoChipLabel(t: TFunction, estado: string): string {
  return estado === 'reportado_arl' ? t('incidentes.estado.chipReportadoArl') : t(`incidentes.estado.${estado}`)
}

export function gravedadChipLabel(t: TFunction, gravedad: string): string {
  return gravedad === 'leve' ? t('incidentes.gravedad.chipLeve') : t(`incidentes.gravedad.${gravedad}`)
}

function pasoEstado(paso: EstadoPaso, actualIdx: number, furatSellado: boolean): PasoEstado {
  const idx = ORDEN.indexOf(paso)
  if (idx > actualIdx) return 'pendiente'
  if (idx === actualIdx) return 'actual'
  // Un accidente puede investigarse sin FURAT aún: su reloj sigue vivo y pintarlo «hecho» mentiría.
  if (paso === 'reportado_arl' && !furatSellado) return 'saltado'
  return 'hecho'
}

const PUNTO: Record<PasoEstado, string> = {
  hecho: 'bg-primary',
  actual: 'bg-primary ring-4 ring-accent',
  saltado: 'bg-tone-amber-fg',
  pendiente: 'bg-border',
}
const ROTULO: Record<PasoEstado, string> = {
  hecho: 'text-muted-foreground',
  actual: 'text-primary',
  saltado: 'text-tone-amber-fg',
  pendiente: 'text-muted-foreground/60',
}

export function EstadoLinea({estado, furatSellado}: {estado: string; furatSellado: boolean}) {
  const {t} = useTranslation()
  const actualIdx = ORDEN.indexOf(estado as EstadoPaso)
  if (actualIdx < 0) return null
  return (
    <ol className="flex items-start gap-1" data-testid="incidente-estado-linea">
      {ORDEN.map((paso, i) => {
        const st = pasoEstado(paso, actualIdx, furatSellado)
        return (
          <li key={paso} className="min-w-0 flex-1" data-testid={`estado-paso-${paso}`} data-estado={st}>
            <div className="flex items-center gap-1" aria-hidden="true">
              <span className={cn('size-2 shrink-0 rounded-full', PUNTO[st])} />
              {i < ORDEN.length - 1 && (
                <span className={cn('h-px min-w-0 flex-1', i < actualIdx ? 'bg-primary/40' : 'bg-border')} />
              )}
            </div>
            <p className={cn('mt-1.5 font-mono text-[10px] leading-tight tracking-wider uppercase', ROTULO[st])}>
              {t(`incidentes.estado.${paso}`)}
            </p>
            {st === 'saltado' && (
              <p className="font-mono text-[10px] leading-tight tracking-wider text-tone-amber-fg uppercase">
                {t('incidentes.linea.sinFurat')}
              </p>
            )}
          </li>
        )
      })}
    </ol>
  )
}
